import { Server } from "@hostingbv/lib/src/api/partner/models/Server";
import { FC, useEffect, useState, } from "react";
import { useRealtimeDeployments } from "./deployments/RealtimeDeployments";
import { useNavigate } from "react-router-dom";
import { DeploymentStatusLabel } from "@hostingbv/lib/src/components/DeploymentStatusLabel";
import { Card } from "@hostingbv/lib/src/components/Card";
import { useApiClient } from "@hostingbv/lib/src/api";
import { config } from "../config";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { DataTable, navigateRow } from "@ventumsoftware/react-toolkit";

export const PleskServerDetails: FC<{ server: Server }> = ({ server }) => {
  return <>
    <DetailsBlock server={server} />
    <SitesOverview server={server} />
  </>
}

function DetailsBlock(props: { server: Server }) {
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)
  const canEdit = can('edit', 'servers', props.server.permissions)
  const [defaultNginxConfig, setDefaultNginxConfig] = useState(props.server?.defaultNginxConfig ?? '')
  const [defaultNginxConfigDirty, setDefaultNginxConfigDirty] = useState(false)
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!

  useEffect(() => {
    setDefaultNginxConfig(props.server?.defaultNginxConfig ?? '')
  }, [props.server?.defaultNginxConfig]);

  const saveDefaultNginxConfig = async () => {
    if (props.server === null) return
    await partnerClient.updateServerDefaultNginxConfig(props.server.id, defaultNginxConfig)
    setDefaultNginxConfigDirty(false)
    await awaitNextDeploymentFinished(props.server.id)
  }

  return <div className={'mb-12'}>
    <Card title={'Details'}>
      <div className={"flex"}>
        <dl className={"flex-1"}>
          <dt className={"text-slate-500 text-xs"}>Name</dt>
          <dd className={"text-slate-900 mb-4"}>{props.server.displayName}</dd>
          <dt className={"text-slate-500 text-xs"}>Support</dt>
          <dd className={"text-slate-900 mb-4"}>{props.server.managedHosting ? "Managed Wordpress" : (props.server.managedServer ? 'Managed Server' : 'Unmanaged Server')}</dd>
        </dl>
      </div>
      {props.server.serverType === "shared_hosting" && <>
        <label>
          <span className={'text-sm font-medium text-brand-900'}>Nginx default.conf</span>
          <textarea
            autoFocus={false}
            className={`border-2 border-slate-200 outline-blue-700 text-black rounded-sm font-normal px-2 py-2 mt-1 h-48 text-xs font-mono w-full`}
            placeholder={canEdit ? 'add_header X-Example "Hello world";' : ''}
            readOnly={!canEdit}
            value={defaultNginxConfig}
            onChange={(e) => {
              setDefaultNginxConfigDirty(true)
              setDefaultNginxConfig(e.target.value);
            }}
          />
          {canEdit && defaultNginxConfigDirty && <Button type={'primary'} size={'sm'} text={'Wijzigingen Opslaan'} onClick={saveDefaultNginxConfig} />}
        </label>
      </>}
    </Card>
  </div>
}

function SitesOverview(props: { server: Server }) {
  const { getRealtimeStatus } = useRealtimeDeployments()!
  const navigate = useNavigate()
  return <>
    <SectionHeader>Sites on this server</SectionHeader>
    <div className={"mb-12"}>
      <DataTable
        keyProperty={'id'}
        data={props.server.sites}
        columns={[
          {
            header: 'Name',
            property: 'displayName',
          },
          {
            header: 'Customer',
            property: 'customer',
            transform: (customer) => customer?.displayName ?? 'n/a',
          },
          {
            header: 'Deployment status',
            property: 'deploymentStatus',
            transform: (status, row) => <DeploymentStatusLabel status={getRealtimeStatus(row.id, status)} />
          },
        ]}
        onClickRow={(row, e) => navigateRow(navigate, `/sites/${row.id}`, e)}
      />
    </div>
  </>
}
